import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { trackCta } from '../analytics';
import { ScrollReveal, StaggerReveal, StaggerItem } from './ScrollReveal';
import BookDemoButton from './BookDemoButton';
import {
  FolderKanban,
  Users,
  CalendarDays,
  ShieldCheck,
  MessageSquare,
  ArrowRight,
  Video,
  Share2,
  Workflow,
  ClipboardCheck,
  Inbox,
  BarChart3,
} from 'lucide-react';
import './Services.css';

const SUPPORTED_PLATFORMS = ['YouTube', 'Instagram', 'TikTok', 'LinkedIn', 'Facebook', 'X', 'Reddit'];

const Features = () => {
  const reduceMotion = useReducedMotion();
  const appBaseUrl = process.env.REACT_APP_APP_URL || 'https://app.pycrust.io';
  const signupUrl = `${appBaseUrl}/login`;
  const features = [
    {
      icon: <Share2 className="ui-icon-feature" />,
      iconClass: 'service-icon-platforms',
      title: 'Supported Platforms',
      description:
        'Connect the accounts your team already publishes on, then schedule and engage from one workspace.',
      details: SUPPORTED_PLATFORMS.map((name) => name),
    },
    {
      icon: <FolderKanban className="ui-icon-feature" />,
      iconClass: 'service-icon-0',
      title: 'Workspaces Create Clean Boundaries',
      description:
        'Separate clients, brands, and campaigns so libraries, accounts, and reviews never mix.',
      details: [
        'Switch between client workspaces from one portfolio',
        'Each workspace keeps its own library, pipeline, and connected accounts',
        'Invite your team and your client into the same workspace',
        'Reuse team templates when you spin up the next client',
      ],
    },
    {
      icon: <Video className="ui-icon-feature" />,
      iconClass: 'service-icon-1',
      title: 'Projects, Files, and Revisions',
      description:
        'Keep planning docs, raw footage, edited media, and drafts together as real assets — not Slack links.',
      details: [
        'Project hubs that group planning, media, and drafts',
        'Foldered file library with uploads and multi-select workflows',
        'Revision history when a cut needs another pass',
        'Deep links from chat, notifications, and to-dos back to the exact item',
      ],
    },
    {
      icon: <Workflow className="ui-icon-feature" />,
      iconClass: 'service-icon-pipeline',
      title: 'Visual Content Pipeline',
      description:
        'See every deliverable move from draft through review, approval, and schedule in one kanban.',
      details: [
        'Stages from internal draft to client review to scheduled and live',
        'Assets, drafts, and schedules stay linked inside the workspace',
        'Calendar view for what is going out next',
        'Fewer handoffs when the whole visual pipeline lives in one place',
      ],
    },
    {
      icon: <ClipboardCheck className="ui-icon-feature" />,
      iconClass: 'service-icon-2',
      title: 'Internal and Client Review Gates',
      description:
        'Route planning, media, and drafts through the right reviewers before anything publishes.',
      details: [
        'Submit for internal review with notes and feedback threads',
        'Optional client sign-off before media, drafts, or publish',
        'Approve or request changes without leaving the asset',
        'Configure which gates apply per workspace',
      ],
    },
    {
      icon: <CalendarDays className="ui-icon-feature" />,
      iconClass: 'service-icon-3',
      title: 'Schedule and Publish Across Platforms',
      description:
        'Turn library assets into platform-ready drafts, then post now or schedule later.',
      details: [
        'Unified composer for captions, assets, and account selection',
        'Format support across YouTube, Instagram, TikTok, LinkedIn, Facebook, X, and Reddit',
        'Reuse one asset across multiple platform drafts',
        'Calendar updates when drafts are scheduled',
      ],
    },
    {
      icon: <Inbox className="ui-icon-feature" />,
      iconClass: 'service-icon-5',
      title: 'Post Feed and Direct Messages',
      description:
        'Stay on top of published posts and conversations without bouncing between native apps.',
      details: [
        'See recent published posts with engagement at a glance',
        'Reply to comments from the post feed',
        'Unified inbox for DMs across connected accounts',
        'Filter by account so each brand stays clear',
      ],
    },
    {
      icon: <BarChart3 className="ui-icon-feature" />,
      iconClass: 'service-icon-4',
      title: 'Publishing Analytics',
      description:
        'Track how posts perform across platforms from the same workspace you publish from.',
      details: [
        'Views, impressions, likes, and comments in one place',
        'Date ranges and per-platform breakdowns',
        'Charts for how work is trending over time',
        'See posts published through Pycrust alongside tracked performance',
      ],
    },
    {
      icon: <Users className="ui-icon-feature" />,
      iconClass: 'service-icon-0',
      title: 'Roles That Match How Agencies Work',
      description:
        'Give producers, editors, reviewers, and clients the access they need — nothing more.',
      details: [
        'Managers, producers, editors, internal reviewers, and clients',
        'Granular permissions for upload, draft, schedule, and review',
        'Clients can leave notes without shared passwords',
        'Personal to-dos and notifications that jump back to the item',
      ],
    },
    {
      icon: <MessageSquare className="ui-icon-feature" />,
      iconClass: 'service-icon-1',
      title: 'In-Workspace Chat With References',
      description:
        'Discuss work where it lives and point teammates at the exact project or file.',
      details: [
        'Workspace chat, groups, and direct messages',
        'Reference projects and files directly in messages',
        'Mention teammates so handoffs are clear',
        'Activity log for what changed in the workspace',
      ],
    },
    {
      icon: <ShieldCheck className="ui-icon-feature" />,
      iconClass: 'service-icon-platforms',
      title: 'Explicit Ownership and Secure Integrations',
      description:
        'Whoever connects an account owns it — always. Official APIs only.',
      details: [
        'Secure OAuth authentication',
        'Multiple accounts per workspace and platform',
        'Reconnect or revoke access without sharing logins',
        'Account status stays visible when a platform needs attention',
      ],
    },
  ];

  return (
    <section id="features" className="services">
      <div className="services-mesh" aria-hidden />

      <div className="container">
        <ScrollReveal direction="up" className="section-header">
          <div className="section-badge">
            <span>Product</span>
          </div>
          <h1 className="section-title">From plan to publish in one workspace</h1>
          <p className="section-subtitle">
            Libraries, reviews, schedules, analytics, and social inbox share the same client
            context — so handoffs do not turn into a thread of {'\u201c'}which file was that?
            {'\u201d'}
          </p>
        </ScrollReveal>

        <StaggerReveal className="services-grid">
          {features.map((feature) => (
            <StaggerItem key={feature.title}>
              <motion.article
                className="service-card"
                whileHover={
                  reduceMotion
                    ? undefined
                    : {
                        y: -6,
                        boxShadow: '0 1rem 2.5rem rgba(31, 111, 235, 0.14)',
                      }
                }
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className={`service-icon ${feature.iconClass}`}>
                  {feature.icon}
                </div>
                <h3 className="service-title">{feature.title}</h3>
                <p className="service-description">{feature.description}</p>
                <ul className="service-features">
                  {feature.details.map((detail) => (
                    <li key={detail} className="service-feature-item">
                      <span className="service-feature-check">✓</span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.article>
            </StaggerItem>
          ))}
        </StaggerReveal>

        <ScrollReveal direction="up" delay={0.1}>
          <div className="services-cta">
            <div className="cta-content">
              <h3>Try it free for 30 days</h3>
              <p>
                Upload assets, connect accounts, route drafts through review, and publish to
                every platform from one workspace. No paid plan required for that first
                workspace.
              </p>

              <div className="cta-buttons cta-actions-row cta-actions-row--center">
                <motion.button
                  className="btn btn-primary"
                  whileHover={reduceMotion ? undefined : { scale: 1.03, y: -2 }}
                  whileTap={reduceMotion ? undefined : { scale: 0.98 }}
                  onClick={() => {
                    trackCta('get_started', 'features_page');
                    window.open(signupUrl, '_blank');
                  }}
                >
                  Get started
                  <ArrowRight className="ui-icon-2xl" />
                </motion.button>
                <BookDemoButton placement="features_page" />
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default Features;
